import { useCallback, useEffect, useRef } from "react";
import { RoomEvent } from "livekit-client";
import { logLiveKit, LogLevel } from "utils/sentryLogger";
import useDevices from "hooks/livekit/useDevices/useDevices";

// ConnectionQuality enum values from livekit-client
const ConnectionQuality = {
  Lost: 0,
  Poor: 1,
  Good: 2,
  Excellent: 3,
};

const createEmptyEventCounts = () => ({
  quality_degraded: 0,
  quality_lost: 0,
  reconnecting: 0,
  reconnected: 0,
  disconnected: 0,
  track_publication_failed: 0,
});

// Module-level tracking to prevent duplicate logs per session
const loggedConnectionEvents = new Set();

// Export for testing purposes
export const resetLoggedConnectionEvents = () => {
  loggedConnectionEvents.clear();
};

// Read navigator.connection info (Network Information API)
const getNetworkInfo = () => {
  const conn = navigator.connection;
  return {
    networkType: conn?.effectiveType || null,
    networkRtt: conn?.rtt ?? null,
    networkDownlink: conn?.downlink ?? null,
  };
};

/**
 * Hook to log LiveKit connection events for debugging and monitoring.
 * Integrates with Sentry for centralized logging.
 *
 * @param {Room|EventEmitter} room - LiveKit room instance
 * @param {Object} options - Options
 * @param {string} options.sessionId - Session ID for context
 * @param {string} options.userId - User ID for context
 * @param {string} options.groupCode - Group code for context
 * @returns {Object} Logging functions
 */
export default function useLiveKitConnectionLogger(room, { sessionId, userId, groupCode } = {}) {
  const {
    audioInputDevices,
    videoInputDevices,
    audioOutputDevices,
    hasAudioInputDevices,
    hasVideoInputDevices,
    hasAudioOutputDevices,
  } = useDevices();

  // Accumulate event counts instead of logging individually
  const eventCountsRef = useRef(createEmptyEventCounts());

  const getDeviceSnapshot = useCallback(() => ({
    audioInputCount: audioInputDevices.length,
    videoInputCount: videoInputDevices.length,
    audioOutputCount: audioOutputDevices.length,
    hasAudioInput: hasAudioInputDevices,
    hasVideoInput: hasVideoInputDevices,
    audioInputDevices,
    videoInputDevices,
    audioOutputDevices,
  }), [
    audioInputDevices,
    videoInputDevices,
    audioOutputDevices,
    hasAudioInputDevices,
    hasVideoInputDevices,
  ]);

  const logConnectionInitiated = useCallback((roomUrl, context = {}) => {
    const sid = context.sessionId || sessionId;
    const uid = context.userId || userId;
    const gc = context.groupCode || groupCode;

    // Prevent duplicate logs for same session
    const eventKey = `initiated_${sid}_${uid}`;
    if (loggedConnectionEvents.has(eventKey)) return;
    loggedConnectionEvents.add(eventKey);

    logLiveKit("LiveKit connection initiated", {
      event: "connection_initiated",
      roomUrl,
      sessionId: sid,
      userId: uid,
      groupCode: gc,
      hasAudioInput: hasAudioInputDevices,
      hasVideoInput: hasVideoInputDevices,
      hasAudioOutput: hasAudioOutputDevices,
      ...getNetworkInfo(),
    });
  }, [sessionId, userId, groupCode, hasAudioInputDevices, hasVideoInputDevices, hasAudioOutputDevices]);

  const logConnectionSuccess = useCallback((localTracks = [], context = {}) => {
    const sid = context.sessionId || sessionId;
    const uid = context.userId || userId;
    const gc = context.groupCode || groupCode;

    // Prevent duplicate logs for same session
    const eventKey = `connected_${sid}_${uid}`;
    if (loggedConnectionEvents.has(eventKey)) return;
    loggedConnectionEvents.add(eventKey);

    const audioTrack = localTracks.find(t => t?.kind === "audio");
    const videoTrack = localTracks.find(t => t?.kind === "video");

    // Get the actual device IDs being used from the tracks
    const audioDeviceId = audioTrack?.mediaStreamTrack?.getSettings?.()?.deviceId;
    const videoDeviceId = videoTrack?.mediaStreamTrack?.getSettings?.()?.deviceId;

    // Find device labels for the active devices
    const activeAudioDevice = audioInputDevices.find(d => d.deviceId === audioDeviceId);
    const activeVideoDevice = videoInputDevices.find(d => d.deviceId === videoDeviceId);

    logLiveKit("LiveKit connection successful", {
      event: "connected",
      sessionId: sid,
      userId: uid,
      groupCode: gc,
      audioConnected: !!audioTrack,
      videoConnected: !!videoTrack,
      activeAudioDevice: activeAudioDevice?.label || audioDeviceId || null,
      activeVideoDevice: activeVideoDevice?.label || videoDeviceId || null,
      connectionQuality: room?.localParticipant?.connectionQuality || null,
      serverAddress: room?.engine?.connectedServerAddress || null,
    });
  }, [sessionId, userId, groupCode, audioInputDevices, videoInputDevices, room]);

  const logConnectionFailure = useCallback((error) => {
    logLiveKit("LiveKit connection failed", {
      level: LogLevel.ERROR,
      event: "connection_failed",
      sessionId,
      errorName: error?.name,
      errorMessage: error?.message,
      errorCode: error?.code,
    });
  }, [sessionId]);

  // Flush accumulated event counts as a single summary log
  const flushConnectionEventsSummary = useCallback((context = {}) => {
    const sid = context.sessionId || sessionId;
    const uid = context.userId || userId;
    const gc = context.groupCode || groupCode;

    // Prevent duplicate flush for same session
    const eventKey = `summary_${sid}_${uid}`;
    if (loggedConnectionEvents.has(eventKey)) return;
    loggedConnectionEvents.add(eventKey);

    const counts = eventCountsRef.current;

    // Determine log level based on severity of events
    let level = LogLevel.INFO;
    if (counts.quality_lost > 0 || counts.track_publication_failed > 0) {
      level = LogLevel.ERROR;
    } else if (counts.quality_degraded > 0 || counts.reconnecting > 0 || counts.disconnected > 0) {
      level = LogLevel.WARNING;
    }

    logLiveKit("LiveKit session connection summary", {
      level,
      event: "session_connection_summary",
      sessionId: sid,
      userId: uid,
      groupCode: gc,
      ...counts,
      connectionQuality: room?.localParticipant?.connectionQuality || null,
    });

    // Reset counts after flushing
    eventCountsRef.current = createEmptyEventCounts();
  }, [sessionId, userId, groupCode, room]);

  // Set up room event listeners
  useEffect(() => {
    if (!room || typeof room.on !== "function") return;

    const handleDisconnected = () => {
      eventCountsRef.current.disconnected += 1;
    };

    const handleReconnecting = () => {
      eventCountsRef.current.reconnecting += 1;
    };

    const handleReconnected = () => {
      eventCountsRef.current.reconnected += 1;
    };

    const handleConnectionQualityChanged = (quality) => {
      // Only track degraded quality (Poor = 1, Lost = 0)
      if (quality === ConnectionQuality.Poor) {
        eventCountsRef.current.quality_degraded += 1;
      } else if (quality === ConnectionQuality.Lost) {
        eventCountsRef.current.quality_lost += 1;
      }
    };

    room.on(RoomEvent.Disconnected, handleDisconnected);
    room.on(RoomEvent.Reconnecting, handleReconnecting);
    room.on(RoomEvent.Reconnected, handleReconnected);
    room.on(RoomEvent.ConnectionQualityChanged, handleConnectionQualityChanged);

    return () => {
      room.off(RoomEvent.Disconnected, handleDisconnected);
      room.off(RoomEvent.Reconnecting, handleReconnecting);
      room.off(RoomEvent.Reconnected, handleReconnected);
      room.off(RoomEvent.ConnectionQualityChanged, handleConnectionQualityChanged);
    };
  }, [room, sessionId]);

  // Set up local participant event listeners for track publication failures
  useEffect(() => {
    if (!room?.localParticipant || typeof room.localParticipant.on !== "function") return;

    const handleTrackPublicationFailed = () => {
      eventCountsRef.current.track_publication_failed += 1;
    };

    room.localParticipant.on("trackPublicationFailed", handleTrackPublicationFailed);

    return () => {
      room.localParticipant.off("trackPublicationFailed", handleTrackPublicationFailed);
    };
  }, [room?.localParticipant, sessionId]);

  return {
    logConnectionInitiated,
    logConnectionSuccess,
    logConnectionFailure,
    getDeviceSnapshot,
    flushConnectionEventsSummary,
  };
}
